import {
    Component,
    ComponentInterface,
    Element,
    Event,
    EventEmitter,
    Host,
    h,
    Listen,
    Method,
    Prop,
    State,
    Watch,
} from "@stencil/core";
import {
    cookieConsentClient,
    CookieSettings,
    cookiePrefOptions,
    CookieSettingsItemLocalized,
} from "../../cookie-consent";
import { getCookieLayerData, CookieLayerData } from "../../api";
import { findTriggerElement, resolvePath } from "./utils";
import {
    CookieLayerViews,
    CookieLayerViewType,
    CookieLayerViewAction,
    CookieLayerTriggers,
} from "./types";
import { Footer } from "./footer";
import { Nav } from "./nav";
import { Controls } from "./controls";
import { debounce } from "../../utils/debounce";
import { getRegisteredGlobal } from "../../utils/getRegisteredGlobal";

@Component({
    tag: "ux-cookie-layer",
    styleUrl: "cookie-layer.scss",
    shadow: false,
})
export class UxCookieLayer implements ComponentInterface {
    /** The dom element */
    @Element() domNode: HTMLUxCookieLayerElement;

    /** Locale in ISO format, used to retrieve localized data */
    @Prop({ reflect: true }) locale: string = "";

    /** The theme that should be applied  */
    @Prop({ reflect: true }) theme: "web" | "responsive" = "web";

    /** The current view id */
    @Prop() view: CookieLayerViewType = CookieLayerViews.INFO;

    /** Indicates if the layer is currently active */
    @State() isActive: boolean = false;

    /** Storage for the data */
    @State() data: CookieLayerData | null = null;

    /** Storage for the local settings */
    @State() cookieSettings: CookieSettings | null = null;

    private modal: HTMLUxCookieLayerModalElement;
    private viewContainer: HTMLDivElement;
    private viewHistory: Array<CookieLayerViewType> = [];
    private cookieSettingsOptions: CookieSettingsItemLocalized[] = cookiePrefOptions.map(
        (option) => ({
            ...option,
            label: "",
            desc: "",
        })
    );

    /** Request data from the API endpoint */
    @Watch("locale")
    handleApiConfig() {
        this.requestData();
    }

    /** Handle active state */
    @Watch("isActive")
    handleActivate() {
        if (this.isActive) {
            this.cookieSettings = cookieConsentClient.getCookieSettings();
            this.viewHistory = [this.view];
            this.scrollToTopDebounced();
            this.cookieLayerShow.emit({
                open: true,
                view: this.view,
            });
        }
    }

    /** Handle view change */
    @Watch("view")
    handleChangeView() {
        if (this.isActive) {
            this.scrollToTop();
            if (this.view === CookieLayerViews.INFO) {
                this.viewHistory = [this.view];
            } else {
                this.viewHistory.push(this.view);
            }
            this.cookieLayerShow.emit({
                open: false,
                view: this.view,
            });
        }
    }

    /** Listen for modal open event  */
    @Listen("uxModalOpen")
    handleModalOpen(event: CustomEvent<string>) {
        if (event.target !== this.modal) {
            return;
        }
        this.isActive = true;
    }

    /** Listen for modal close event  */
    @Listen("uxModalClose")
    handleModalClose(event: CustomEvent<string>) {
        if (event.target !== this.modal) {
            return;
        }
        this.isActive = false;
    }

    /** Emitted when user clicks the consent or save settings button */
    @Event({ eventName: "uxCookieSettings", bubbles: true, cancelable: false })
    cookieSettingsChanged: EventEmitter<CookieSettings>;

    /** Emitted when cookie layer is opened or user switches the view */
    @Event({ eventName: "uxCookieLayerShow", bubbles: true, cancelable: false })
    cookieLayerShow: EventEmitter<CookieLayerViewAction>;

    /** Open CookieLayer and show a specific view  */
    @Method()
    async show(view: CookieLayerViewType): Promise<void> {
        if (
            this.isActive &&
            this.viewContainer !== undefined &&
            view !== this.view
        ) {
            this.viewContainer.style.minHeight = `${this.viewContainer.offsetHeight}px`;
            window.setTimeout(() => {
                this.viewContainer.style.minHeight = "0";
            }, 20);
        }
        this.view = view;
        this.isActive = true;
    }

    private scrollToTop = () => {
        const element: HTMLDivElement | null = this.domNode.querySelector(
            ".ux-cookie-layer-content"
        );
        if (element !== null) {
            element.scrollTop = 0;
        }
    };

    private scrollToTopDebounced = debounce(this.scrollToTop, 20);

    private handleConsentRequiredOnly = () => {
        if (this.cookieSettings === null) {
            return;
        }
        this.cookieSettings.required = true;
        this.cookieSettings.optional = false;
        cookieConsentClient.consent(this.cookieSettings);
        this.cookieSettingsChanged.emit(this.cookieSettings);
        this.isActive = false;
    };

    private handleConsentAll = () => {
        if (this.cookieSettings === null) {
            return;
        }
        this.cookieSettings.required = true;
        this.cookieSettings.optional = true;
        cookieConsentClient.consent(this.cookieSettings);
        this.cookieSettingsChanged.emit(this.cookieSettings);
        this.isActive = false;
    };

    private handleSaveSettings = () => {
        if (this.cookieSettings === null) {
            return;
        }
        this.cookieSettings.required = true;
        cookieConsentClient.consent(this.cookieSettings);
        this.cookieSettingsChanged.emit(this.cookieSettings);
        this.isActive = false;
    };

    private handleSettingsSwitch = (e: CustomEvent) => {
        if (this.cookieSettings === null) {
            return;
        }
        const switchElement = e.target as HTMLUxCookieLayerSwitchElement;
        if (typeof this.cookieSettings[switchElement.name] !== "undefined") {
            this.cookieSettings[switchElement.name] = e.detail === true;
        }
    };

    private handleSettingsLink = (e: Event) => {
        e.preventDefault();
        this.show(CookieLayerViews.SETTINGS);
    };

    private handleDocumentClick = (e: MouseEvent) => {
        const targetElement = findTriggerElement(
            e.target as HTMLElement,
            "[data-ux-cookie-layer]"
        );
        if (targetElement === null) {
            return;
        }
        e.preventDefault();
        const action = targetElement.getAttribute("data-ux-cookie-layer");
        switch (action) {
            case CookieLayerTriggers.INFO:
                this.view = CookieLayerViews.INFO;
                this.isActive = true;
                break;
            case CookieLayerTriggers.SETTINGS:
                this.view = CookieLayerViews.SETTINGS;
                this.isActive = true;
                break;
            case CookieLayerTriggers.ACCEPT_REQUIRED:
                if (this.isActive) {
                    this.handleConsentRequiredOnly();
                }
                break;
            default:
                break;
        }
    };

    private requestData(): void {
        getCookieLayerData(this.locale).then((data) => {
            if (data === undefined) {
                return;
            }
            this.data = data;
            this.cookieSettingsOptions = cookiePrefOptions.map((option) => ({
                ...option,
                label: this.getText(option.i18nLabel),
                desc: this.getText(option.i18nDesc),
            }));
            this.activateIfRequired();
        });
    }

    private getText = (key: string): string => {
        if (this.data === null || typeof this.data.labels !== "object") {
            return key;
        }
        const value = resolvePath(key, this.data.labels);
        return typeof value === "string" ? value : key;
    };

    private activateIfRequired = (): void => {
        if (!cookieConsentClient.hasConsented()) {
            if (getRegisteredGlobal("trumpf.ux.disableCookieLayer") === true) {
                return;
            }
            // Legacy disableCookieLayer flag
            if (window["disableCookieLayer"] === true) {
                return;
            }
            this.isActive = true;
        }
    };

    componentWillLoad() {
        this.cookieSettings = cookieConsentClient.getCookieSettings();
    }

    componentDidLoad() {
        if (this.data === null) {
            this.requestData();
        } else {
            this.activateIfRequired();
        }
    }

    connectedCallback() {
        document.addEventListener("click", this.handleDocumentClick);
    }

    disconnectedCallback() {
        document.removeEventListener("click", this.handleDocumentClick);
    }

    render() {
        const hasConsented = this.cookieSettings.required;
        const baseClass = "ux-cookie-layer";
        const hasNav = this.data?.links.length > 0;
        const t = this.getText;
        return (
            <Host class={baseClass} onUxSwitch={this.handleSettingsSwitch}>
                <ux-cookie-layer-theme theme={this.theme} />
                <ux-cookie-layer-modal
                    name="ux-cookie-consent-modal"
                    opened={this.isActive}
                    no-close-button={!hasConsented}
                    ref={(el) => (this.modal = el)}
                >
                    <ux-cookie-layer-content>
                        <div
                            class={`${baseClass}__view`}
                            data-view={this.view}
                            ref={(el) => (this.viewContainer = el)}
                        >
                            <h3 class={`${baseClass}__title`}>
                                {t(`${this.view}Title`)}
                            </h3>
                            <div
                                class={`${baseClass}__content`}
                                innerHTML={t(`${this.view}Content`)}
                            />
                            {this.view === CookieLayerViews.SETTINGS && (
                                <Controls
                                    baseClass={baseClass}
                                    options={this.cookieSettingsOptions}
                                    settings={this.cookieSettings}
                                />
                            )}
                        </div>
                    </ux-cookie-layer-content>
                    {this.view === CookieLayerViews.INFO && (
                        <Footer
                            primaryButtonLabel={t("btnConsent")}
                            secondaryButtonLabel={t("btnSettings")}
                            secondaryButtonType="link"
                            onPrimaryButtonClick={this.handleConsentAll}
                            onSecondaryButtonClick={this.handleSettingsLink}
                        />
                    )}
                    {this.view === CookieLayerViews.SETTINGS && (
                        <Footer
                            primaryButtonLabel={t("btnSaveSettings")}
                            secondaryButtonLabel={t(
                                this.viewHistory.length > 1
                                    ? "btnBack"
                                    : "btnClose"
                            )}
                            onPrimaryButtonClick={this.handleSaveSettings}
                            onSecondaryButtonClick={
                                this.viewHistory.length > 1
                                    ? () => this.show(this.viewHistory[0])
                                    : () => (this.isActive = false)
                            }
                        />
                    )}
                    {hasNav && (
                        <Nav baseClass={baseClass} links={this.data.links} />
                    )}
                </ux-cookie-layer-modal>
            </Host>
        );
    }
}
